import { useMemo, useCallback, useState } from "react";

import { ColumnConfig, loadStoredConfig, reconcileColumnKeys } from "./utils";

export type ConfigurableColumn = {
  key: string;
  name: string;
  resizable?: boolean;
  width?: string;
  // Column-settings groups that toggle this column with others, outermost first.
  groups?: string[];
};

const NO_KEYS: string[] = [];

export const useTopBuildingsListConfig = (
  storageKey: string,
  allColumns: ConfigurableColumn[],
  // Hidden until the user shows them; a saved layout that already places a column overrides its default.
  defaultHiddenKeys: string[] = NO_KEYS,
) => {
  const [stored, setStored] = useState(() => ({ storageKey, config: loadStoredConfig(storageKey) }));
  if (stored.storageKey !== storageKey) {
    setStored({ storageKey, config: loadStoredConfig(storageKey) });
  }
  const config = stored.config;

  const allKeys = useMemo(() => allColumns.map((c) => c.key), [allColumns]);

  const groupsByKey = useMemo(() => new Map(allColumns.map((c) => [c.key, c.groups ?? []])), [allColumns]);

  const orderedKeys = useMemo(
    () => reconcileColumnKeys(config.orderedKeys, allKeys, (key) => groupsByKey.get(key) ?? []),
    [config.orderedKeys, allKeys, groupsByKey],
  );

  const applyConfig = useCallback(
    (newConfig: ColumnConfig) => {
      setStored({ storageKey, config: newConfig });
      localStorage.setItem(storageKey, JSON.stringify(newConfig));
    },
    [storageKey],
  );

  const resetToDefault = useCallback(() => {
    setStored({ storageKey, config: { orderedKeys: [], hiddenKeys: [] } });
    localStorage.removeItem(storageKey);
  }, [storageKey]);

  const defaultHiddenSet = useMemo(() => new Set(defaultHiddenKeys), [defaultHiddenKeys]);

  // A column the saved layout doesn't know yet (or no saved layout at all) takes its default visibility.
  const hiddenSet = useMemo(() => {
    const placed = new Set(config.orderedKeys);
    return new Set([...config.hiddenKeys, ...defaultHiddenKeys.filter((key) => !placed.has(key))]);
  }, [config.orderedKeys, config.hiddenKeys, defaultHiddenKeys]);

  const isCustomized = useMemo(
    () =>
      allKeys.some((key) => hiddenSet.has(key) !== defaultHiddenSet.has(key)) ||
      orderedKeys.some((key, i) => key !== allKeys[i]),
    [allKeys, hiddenSet, defaultHiddenSet, orderedKeys],
  );

  const configuredBaseColumns = useMemo(
    () =>
      orderedKeys
        .map((key) => allColumns.find((c) => c.key === key))
        .filter((c): c is ConfigurableColumn => !!c && !hiddenSet.has(c.key)),
    [orderedKeys, allColumns, hiddenSet],
  );

  const allColumnDefs = useMemo(
    () =>
      orderedKeys
        .map((key) => allColumns.find((c) => c.key === key))
        .filter((c): c is ConfigurableColumn => !!c)
        .map((c) => ({ ...c, isVisible: !hiddenSet.has(c.key) })),
    [orderedKeys, allColumns, hiddenSet],
  );

  return {
    configuredBaseColumns,
    allColumnDefs,
    applyConfig,
    resetToDefault,
    isCustomized,
  };
};
